import { useSafariPinchZoom } from '../use-safari-pinch-zoom'
import { useViewportZoomCommands } from '../use-viewport-zoom-commands'
import type {
  CanvasFoundation,
  CanvasPointerGestures,
  CanvasViewport,
} from '../../types/canvas-groups'
import type { ResolvedCenterCanvasConfig } from '../../types/resolved-center-canvas-config'

/**
 * Zoom commands for the toolbar and menus (fit width, zoom to selection, zoom in/out, centering)
 * and Safari trackpad pinch zoom.
 */
export function useCanvasZoomGestures(
  { onZoomChange, bleedWidth }: ResolvedCenterCanvasConfig,
  {
    internalShapeManagerRef,
    scrollRef,
    pasteboardRef,
  }: Pick<CanvasFoundation, 'internalShapeManagerRef' | 'scrollRef' | 'pasteboardRef'>,
  {
    zoomRef,
    scaleRef,
    onZoomChangeRef,
    fitContentDimensions,
    getCanvasOrigin,
    computeContentPointAtClient,
    syncRulerScrollTransforms,
    pendingCtrlWheelAnchorRef,
  }: Pick<
    CanvasViewport,
    | 'zoomRef'
    | 'scaleRef'
    | 'onZoomChangeRef'
    | 'fitContentDimensions'
    | 'getCanvasOrigin'
    | 'computeContentPointAtClient'
    | 'syncRulerScrollTransforms'
    | 'pendingCtrlWheelAnchorRef'
  >,
  { applyZoomToCanvasRectRef }: Pick<CanvasPointerGestures, 'applyZoomToCanvasRectRef'>
) {
  useSafariPinchZoom({
    scrollRef,
    zoomRef,
    scaleRef,
    onZoomChangeRef,
    pendingCtrlWheelAnchorRef,
    syncRulerScrollTransforms,
    computeContentPointAtClient,
  })

  const {
    centerViewportOnSelectionOrSpread,
    fitWidth,
    zoomToFitSelection,
    zoomInFromToolbar,
    zoomOutFromToolbar,
  } = useViewportZoomCommands({
    scrollRef,
    pasteboardRef,
    internalShapeManagerRef,
    zoomRef,
    scaleRef,
    applyZoomToCanvasRectRef,
    onZoomChange,
    getCanvasOrigin,
    syncRulerScrollTransforms,
    fitContentDimensions,
    bleedWidth,
  })

  return {
    centerViewportOnSelectionOrSpread,
    fitWidth,
    zoomToFitSelection,
    zoomInFromToolbar,
    zoomOutFromToolbar,
  }
}
